import { z } from 'zod';
import type {
  CalculateInvoicePayload,
  CustomerInvoice,
  InvoiceTrackingEntry,
} from '@/apis/invoices';
import { CARRIER_OPTIONS } from '@/pages/Warehouse/warehouse-shared';

const amount = (allowNegative = false) =>
  z
    .string()
    .optional()
    .refine(
      (v) => !v || (!isNaN(Number(v)) && (allowNegative || Number(v) >= 0)),
      'Enter a valid amount',
    );

/**
 * One inbound/outbound shipment — same fields as the warehouse Inbound
 * Shipment dialog, plus its cost. `carrier` is a CARRIER_OPTIONS value; when
 * it is "Other" the name is typed into `carrierOther`.
 */
const shippingEntrySchema = z
  .object({
    carrier: z.string(),
    carrierOther: z.string(),
    trackingNo: z.string(),
    cost: amount(),
    managerId: z.string(),
    note: z.string(),
  })
  // A cost or note without a tracking number used to be dropped silently on save.
  .refine((e) => !!e.trackingNo.trim() || (!e.cost && !e.note.trim()), {
    message: 'Enter the tracking or PO number',
    path: ['trackingNo'],
  })
  .refine((e) => e.carrier !== 'Other' || !!e.carrierOther.trim(), {
    message: 'Enter the carrier name',
    path: ['carrierOther'],
  });

export const invoiceItemSchema = z
  .object({
  // Optional so a part number the catalog has never seen can still be invoiced.
  productId: z.string().optional(),
  productName: z.string().optional(),
  image: z.string().optional(),
  condition: z.string().optional(),
  weight: amount(),
  quantity: z
    .string()
    .refine(
      (v) => Number.isInteger(Number(v)) && Number(v) >= 1,
      'Enter a valid quantity',
    ),
  // Negative rates are how core returns are entered on an otherwise normal invoice.
  rate: amount(true),
  discount: z
    .string()
    .optional()
    .refine(
      (v) => !v || (!isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100),
      'Discount must be between 0 and 100',
    ),
  /**
   * No longer editable — the invoice-level Sales Tax replaced the Tax column.
   * Still round-tripped so an invoice saved with per-item rates keeps its tax
   * until someone picks an invoice-level rate.
   */
  taxRate: z.string().optional(),
  })
  .refine((v) => !!v.productId?.trim() || !!v.productName?.trim(), {
    message: 'Pick an item or enter a part number',
    path: ['productId'],
  });

export const invoiceFormSchema = z
  .object({
  customerId: z.string().min(1, 'Select a customer'),
  emailAddress: z
    .string()
    .optional()
    .refine((v) => !v || /\S+@\S+\.\S+/.test(v), 'Enter a valid email'),
  issueDate: z.string().min(1, 'Required'),
  dueDate: z.string().optional(),
  terms: z.string().optional(),
  salesperson: z.string().optional(),
  leadSource: z.string().optional(),
  deliveryCost: amount(),
  suppliers: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      cost: amount(),
      files: z.array(z.string()),
    }),
  ),
  inboundShipping: z.array(shippingEntrySchema),
  outboundShipping: z.array(shippingEntrySchema),
  accountingMonth: z.string().optional(),
  accountingCost: amount(),
  saleComplete: z.boolean(),
  shippingCharges: amount(),
  shippingTaxRate: z.string().optional(),
  /** One rate for every line, replacing the per-item Tax column. */
  salesTaxRate: z.string().optional(),
  bankWireFee: amount(),
  termsAndConditions: z.string().optional(),
  customerNotes: z.string().optional(),
  attachedFiles: z.array(z.string()),
  documentType: z.enum(['invoice', 'credit_memo']).optional(),
  relatedInvoiceId: z.string().optional(),
  creditReason: z.string().optional(),
  // The real union, not z.string(): a loose string would let an invoice-only
  // status reach a credit memo through the form.
  status: z
    .enum([
      'draft',
      'sent',
      'paid',
      'partially_paid',
      'overdue',
      'cancelled',
      'partially_applied',
      'closed',
      'voided',
    ])
    .optional(),
  items: z.array(invoiceItemSchema).min(1, 'Add at least one item'),
  })
  .refine(
    (v) =>
      v.documentType === 'credit_memo' ||
      !v.dueDate ||
      !v.issueDate ||
      v.dueDate >= v.issueDate,
    {
      message: 'Due date must be on or after the invoice date',
      path: ['dueDate'],
    },
  );

export type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;
export type InvoiceItemValues = InvoiceFormValues['items'][number];
export type InvoiceSupplierValues = InvoiceFormValues['suppliers'][number];
export type ShippingEntryValues = InvoiceFormValues['inboundShipping'][number];

/** Total Supplier Cost is a roll-up, never typed in — the backend sums it too. */
export const sumSupplierCost = (suppliers: InvoiceSupplierValues[]) =>
  suppliers.reduce((total, s) => total + (toNum(s.cost) ?? 0), 0);

/**
 * Amount stays a derived column, but typing into it works backwards to a Rate so
 * the line always satisfies Amount = Qty x Rate - Discount. Amount is pre-tax —
 * the backend taxes the line total separately — so taxRate is not part of this.
 * Returns null when no rate could produce that amount.
 */
export const rateFromAmount = (
  amount: number,
  quantity?: string,
  discount?: string,
): string | null => {
  const qty = toNum(quantity);
  if (!qty || qty <= 0 || !Number.isFinite(amount)) return null;
  const afterDiscount = 1 - (toNum(discount) ?? 0) / 100;
  // A 100% discount zeroes every line total, so no rate can produce the amount.
  if (afterDiscount <= 0) return null;
  // `rate` is decimal(12,2) in the database. Rounding here keeps the grid
  // honest: the user sees the amount that will actually be saved, rather than
  // it drifting a cent once the extra precision is dropped.
  return (amount / (qty * afterDiscount)).toFixed(2);
};

/**
 * A stored rate back into the picklist's own spelling.
 *
 * The column is `decimal(5,2)`, so Postgres hands back `"10.00"` for what the
 * options call `"10"` — Radix matches by exact value, so without this the
 * picklist reopens blank and the next save drops the rate.
 */
export const toRateOption = (v?: string | null) => {
  if (v === undefined || v === null || v === '') return '';
  const parsed = Number(v);
  return Number.isFinite(parsed) ? String(parsed) : v;
};

export const toNum = (v?: string) =>
  v !== undefined && v !== '' && !isNaN(Number(v)) ? Number(v) : undefined;

export const buildCalcPayload = (
  v: InvoiceFormValues,
): CalculateInvoicePayload => ({
  items: v.items.map((it) => ({
    productId: it.productId?.trim() || undefined,
    quantity: toNum(it.quantity) ?? 1,
    rate: toNum(it.rate),
    discount: toNum(it.discount),
    discountType: 'PERCENTAGE',
    taxRate: toNum(it.taxRate),
  })),
  shippingCharges: toNum(v.shippingCharges),
  bankWireFee: toNum(v.bankWireFee),
  shippingTaxRate: toNum(v.shippingTaxRate),
  salesTaxRate: toNum(v.salesTaxRate),
});

export const emptyItem: InvoiceItemValues = {
  productId: '',
  productName: '',
  image: '',
  condition: '',
  weight: '',
  quantity: '1',
  rate: '',
  discount: '',
  taxRate: '',
};

export const TERMS_OPTIONS = [
  'Due on Receipt',
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60',
] as const;

export const LEAD_SOURCE_OPTIONS = ['website', 'referral', 'marketplace'] as const;

export const TAX_RATE_OPTIONS = ['4', '5', '6', '6.5', '7', '7.5', '8', '8.25', '9', '10'] as const;

export const DEFAULT_TERMS_TEXT =
  'Unless other arrangements have been made in writing prior to purchase, all return items are subject to a 25% restocking fee. Electrical items and invoiced items marked "Non-returnable" are not returnable. Returned goods must be in their original packaging, not installed and returned within 30 days of delivery. Shipping & handling charges are not refundable. Buyer is responsible for all return shipping / insurance costs. Please contact us prior to your return for an RGA (Return of Goods Authorization).';

/** Invoices saved before per-supplier cost/files hold a `{ name: value }` object. */
const toSupplierValues = (
  suppliers: CustomerInvoice['suppliers'],
): InvoiceSupplierValues[] => {
  const rows = Array.isArray(suppliers)
    ? suppliers
    : Object.entries(suppliers ?? {}).map(([name, value]) => ({
        name,
        value: String(value),
      }));
  return rows.map((s) => ({
    name: s.name ?? '',
    value: s.value ?? '',
    cost: 'cost' in s && s.cost !== undefined ? String(s.cost) : '',
    files: ('files' in s && s.files) || [],
  }));
};

export const emptyShippingEntry = (): ShippingEntryValues => ({
  carrier: CARRIER_OPTIONS[0],
  carrierOther: '',
  trackingNo: '',
  cost: '',
  managerId: '',
  note: '',
});

/**
 * `outbound` is absent on every invoice saved before the inbound/outbound
 * split, and entries saved before carriers existed have no `carrier`.
 */
const toShippingValues = (
  entries: InvoiceTrackingEntry[] | undefined,
): ShippingEntryValues[] =>
  (entries ?? []).map((t) => {
    const carrier = t.carrier?.trim() ?? '';
    const isKnown = (CARRIER_OPTIONS as readonly string[]).includes(carrier);
    return {
      carrier: !carrier || isKnown ? carrier : 'Other',
      carrierOther: carrier && !isKnown ? carrier : '',
      trackingNo: t.trackingNo ?? '',
      cost: t.cost !== undefined ? String(t.cost) : '',
      managerId: t.managerId ?? '',
      note: t.note ?? '',
    };
  });

/** Form entries → stored trackingData entries (blank rows dropped). */
export const toTrackingEntries = (
  entries: ShippingEntryValues[],
): InvoiceTrackingEntry[] =>
  entries
    .filter((t) => t.trackingNo.trim())
    .map((t) => {
      const carrier =
        t.carrier === 'Other' ? t.carrierOther.trim() : t.carrier;
      return {
        ...(carrier && { carrier }),
        trackingNo: t.trackingNo.trim(),
        cost: toNum(t.cost),
        ...(t.managerId && { managerId: t.managerId }),
        ...(t.note.trim() && { note: t.note.trim() }),
      };
    });

export const invoiceToFormValues = (
  invoice: CustomerInvoice,
): InvoiceFormValues => ({
  customerId: invoice.customerId,
  emailAddress: invoice.emailAddress ?? invoice.customer?.email ?? '',
  issueDate: invoice.issueDate?.slice(0, 10) ?? '',
  dueDate: invoice.dueDate?.slice(0, 10) ?? '',
  terms: invoice.terms ?? '',
  salesperson: invoice.salesperson ?? '',
  leadSource: invoice.leadSource ?? '',
  deliveryCost: invoice.deliveryCost ?? '',
  suppliers: toSupplierValues(invoice.suppliers),
  inboundShipping: toShippingValues(invoice.trackingData?.inbound),
  outboundShipping: toShippingValues(invoice.trackingData?.outbound),
  accountingMonth: invoice.accountingMonth?.slice(0, 7) ?? '',
  accountingCost: invoice.accountingCost ?? '',
  saleComplete: invoice.saleComplete ?? false,
  shippingCharges: invoice.shippingCharges ?? '',
  shippingTaxRate: toRateOption(invoice.shippingTaxRate),
  salesTaxRate: toRateOption(invoice.salesTaxRate),
  bankWireFee: invoice.bankWireFee ?? '',
  termsAndConditions: invoice.termsAndConditions ?? DEFAULT_TERMS_TEXT,
  customerNotes: invoice.customerNotes ?? '',
  attachedFiles: invoice.attachedFiles ?? [],
  documentType: invoice.documentType ?? 'invoice',
  relatedInvoiceId: invoice.relatedInvoiceId ?? '',
  creditReason: invoice.creditReason ?? '',
  status: invoice.status ?? 'draft',
  items: invoice.items?.length
    ? invoice.items.map((it) => ({
        productId: it.productId ?? '',
        productName: it.productName ?? '',
        image: '',
        condition: it.condition ?? '',
        weight: it.weight ?? '',
        quantity: String(it.quantity),
        rate: it.rate ?? '',
        discount: it.discount ?? '',
        taxRate: toRateOption(it.taxRate),
      }))
    : [emptyItem],
});

export const emptyFormValues = (): InvoiceFormValues => ({
  customerId: '',
  emailAddress: '',
  issueDate: new Date().toISOString().slice(0, 10),
  dueDate: '',
  terms: 'Due on Receipt',
  salesperson: '',
  leadSource: '',
  deliveryCost: '',
  suppliers: [],
  inboundShipping: [],
  outboundShipping: [],
  accountingMonth: '',
  accountingCost: '',
  saleComplete: false,
  shippingCharges: '',
  shippingTaxRate: '',
  salesTaxRate: '',
  bankWireFee: '',
  termsAndConditions: DEFAULT_TERMS_TEXT,
  customerNotes: '',
  attachedFiles: [],
  documentType: 'invoice' as const,
  relatedInvoiceId: '',
  creditReason: '',
  status: 'draft' as const,
  items: [emptyItem],
});
